import type { NestedKeys } from '../../types.ts';

/**
 * `en` · `settings` namespace — the theme / language / timezone override panel.
 *
 * `locale.options` is intentionally keyed by language tag: a locale the product
 * does not ship strings for must not appear in the picker, so this map is the
 * display-name source for `SUPPORTED_LOCALES` and grows with it, one entry per
 * shipped locale — never a hardcoded list in a component.
 */
export const settings = {
  title: 'Settings',
  autoDetect: 'Auto-detect',
  saving: 'Saving…',
  saved: 'Saved',
  saveFailed: "Couldn't save your preferences.",
  theme: {
    label: 'Theme',
    options: {
      auto: 'System',
      light: 'Light',
      dark: 'Dark',
    },
  },
  locale: {
    label: 'Language',
    options: {
      en: 'English',
    },
  },
  timezone: {
    label: 'Time zone',
    auto: 'Auto-detect ({zone})',
  },
  /**
   * The self-serve data-rights controls (compliance.md § "Data rights"). The
   * delete copy is deliberately blunt about the two things a user cannot
   * discover by pressing it: they are signed out immediately, and there is no
   * in-product undo during the grace window.
   */
  dataRights: {
    title: 'Your data',
    description:
      'Download everything we hold about you, or ask us to erase it. Both are self-serve — no support ticket needed.',
    export: 'Download my data',
    exported: 'Saved as {file}',
    cooldown: 'You just downloaded an export. Try again in a few minutes.',
    delete: 'Delete my account',
    deleteConfirm: 'Yes, delete my account',
    deleteCancel: 'Keep my account',
    deleteWarning:
      'This signs you out straight away and starts a 30-day countdown, after which your companions, chats, memories and media are erased for good. You cannot undo this from the app — write to support if you change your mind.',
    scheduled:
      'Your account is scheduled for deletion and is already signed out everywhere. Your data is erased on {date}.',
    signOut: 'Sign out',
    working: 'Working…',
  },
} as const;

export type SettingsMessages = typeof settings;

/** Typo-safe dot-path keys for the `settings` namespace. */
export type SettingsKey = NestedKeys<SettingsMessages>;
